/**
 * CartContext – Central cart + wishlist state for the Hauptstadtstickerei shop.
 * - Guest carts use a stable session_id (uuid) stored in localStorage.
 * - On login, guest cart is merged into user cart.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function ensureSessionId() {
  try {
    let sid = localStorage.getItem('hs_session_id');
    if (!sid) {
      sid = crypto.randomUUID?.() || `sid_${Math.random().toString(36).slice(2)}_${Date.now()}`;
      localStorage.setItem('hs_session_id', sid);
    }
    return sid;
  } catch (_e) {
    return `sid_${Date.now()}`;
  }
}

function getAuthHeaders() {
  const t = localStorage.getItem('token') || localStorage.getItem('admin_token');
  const h = { 'X-Hs-Session': ensureSessionId() };
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cart, setCart] = useState({ items: [] });
  const [wishlist, setWishlist] = useState({ items: [] });
  const [cartOpen, setCartOpen] = useState(false);
  const [wishlistOpen, setWishlistOpen] = useState(false);
  const loadedRef = useRef(false);

  const reloadCart = useCallback(async () => {
    try {
      // Bei Gästen: die aus dem POST /cart/add etablierte Session-ID (localStorage)
      // als Query-Parameter mitgeben, damit der Server dieselbe Cart findet
      // — auch wenn Third-Party-Cookies blockiert sind.
      const sid = typeof window !== 'undefined' ? window.localStorage.getItem('shop_session_id') : null;
      const res = await axios.get(`${API}/api/shop/cart`, {
        headers: getAuthHeaders(),
        params: sid ? { session_id: sid } : undefined,
        withCredentials: true,
      });
      const nextCart = res.data?.cart || { items: [] };
      // Totals ins Cart-Objekt hängen, damit MwSt-Aufschlüsselung UI-weit verfügbar ist
      nextCart.totals = res.data?.totals || null;
      setCart(nextCart);
    } catch (_err) {
      // silent
    }
  }, []);

  const reloadWishlist = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/shop/wishlist`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setWishlist(res.data?.wishlist || { items: [] });
    } catch (_err) {
      // silent
    }
  }, []);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    reloadCart();
    reloadWishlist();
  }, [reloadCart, reloadWishlist]);

  // Iter252: Custom-Blocks (z.B. Calculator „In den Warenkorb") triggern
  // ein window-Event, wenn sie direkt am Server persistiert haben.
  useEffect(() => {
    const onRefresh = () => reloadCart();
    window.addEventListener('hs:cart:refresh', onRefresh);
    return () => window.removeEventListener('hs:cart:refresh', onRefresh);
  }, [reloadCart]);

  const addToCart = useCallback(
    async (payload) => {
      try {
        const res = await axios.post(
          `${API}/api/shop/cart/add`,
          { ...payload, session_id: ensureSessionId() },
          { headers: getAuthHeaders(), withCredentials: true }
        );
        const nc = res.data?.cart || { items: [] };
        nc.totals = res.data?.totals || null;
        setCart(nc);
        toast.success('In den Warenkorb gelegt', { description: payload.product_name || '' });
        // Feb 2026 · Phase D — Analytics
        try { import('@/utils/analytics').then(({ trackEvent }) => trackEvent('add_to_cart', { product_id: payload.product_id })); } catch { /* noop */ }
        return res.data;
      } catch (err) {
        const d = err?.response?.data?.detail;
        const msg =
          typeof d === 'string'
            ? d
            : (d && (d.message || d.msg)) || 'Konnte nicht in den Warenkorb gelegt werden';
        // Wenn Aufrufer (z.B. CartCrossSell) 409/OUT_OF_STOCK selbst behandelt,
        // unterdrücken wir hier den Toast, damit keine Doppel-Meldung entsteht.
        const status = err?.response?.status;
        const code = typeof d === 'object' && d !== null ? d.code : null;
        if (status !== 409 && code !== 'OUT_OF_STOCK') {
          toast.error(msg);
        }
        throw err;
      }
    },
    []
  );

  const updateCartItem = useCallback(async (itemId, quantity) => {
    try {
      const res = await axios.patch(
        `${API}/api/shop/cart/item/${itemId}`,
        { quantity, session_id: ensureSessionId() },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      const nc = res.data?.cart || { items: [] };
      nc.totals = res.data?.totals || null;
      setCart(nc);
      // Nach Mengen-Änderung: Rabatt neu berechnen lassen
      await reloadCart();
    } catch (err) {
      toast.error('Warenkorb konnte nicht aktualisiert werden');
    }
  }, [reloadCart]);

  const removeCartItem = useCallback(async (itemId) => {
    try {
      const sid = ensureSessionId();
      const res = await axios.delete(`${API}/api/shop/cart/item/${itemId}`, {
        headers: getAuthHeaders(),
        params: { session_id: sid },
        withCredentials: true,
      });
      const nc = res.data?.cart || { items: [] };
      nc.totals = res.data?.totals || null;
      setCart(nc);
      await reloadCart();
    } catch (err) {
      toast.error('Artikel konnte nicht entfernt werden');
    }
  }, [reloadCart]);

  const addToWishlist = useCallback(async (payload) => {
    try {
      const res = await axios.post(
        `${API}/api/shop/wishlist/add`,
        { ...payload, session_id: ensureSessionId() },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setWishlist(res.data?.wishlist || { items: [] });
      toast.success('Auf Merkzettel gespeichert', { description: payload.product_name || '' });
      return res.data;
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : (err?.response?.data?.detail?.message || 'Konnte nicht gespeichert werden'));
      throw err;
    }
  }, []);

  const removeWishlistItem = useCallback(async (itemId) => {
    try {
      const sid = ensureSessionId();
      const res = await axios.delete(`${API}/api/shop/wishlist/item/${itemId}`, {
        headers: getAuthHeaders(),
        params: { session_id: sid },
        withCredentials: true,
      });
      setWishlist(res.data?.wishlist || { items: [] });
    } catch (_err) {
      // silent
    }
  }, []);

  const mergeGuestCart = useCallback(async () => {
    // Called after login
    try {
      const sid = localStorage.getItem('hs_session_id');
      if (!sid) return;
      await axios.post(
        `${API}/api/shop/cart/merge`,
        { session_id: sid },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      await reloadCart();
    } catch (_err) {
      // silent
    }
  }, [reloadCart]);

  const cartCount = cart.items?.reduce((s, it) => s + (it.quantity || 0), 0) || 0;
  const wishlistCount = wishlist.items?.length || 0;
  const subtotalCents = cart.items?.reduce((s, it) => s + (it.line_total_cents || 0), 0) || 0;
  const discountCents = cart.discount_cents || 0;
  const vipDiscountCents = cart.totals?.vip_discount_cents || 0;
  const vipPercent = cart.totals?.vip_percent || 0;
  const totalCents = Math.max(0, subtotalCents - discountCents - vipDiscountCents - (cart.voucher_value_cents || 0));
  const netCents = cart.totals?.net_cents ?? Math.round(totalCents / 1.19);
  const taxCents = cart.totals?.tax_cents ?? (totalCents - netCents);
  const taxBreakdown = cart.totals?.tax_breakdown || [];
  const couponInfo = cart.coupon_info || null;
  // Voucher (Gutschein-Code) — separate von Coupon-Codes.
  const voucherCode = cart.voucher_code || null;
  const voucherCents = Math.min(cart.voucher_value_cents || 0, Math.max(0, subtotalCents - discountCents));

  const applyVoucher = useCallback(async (code) => {
    try {
      const res = await axios.post(
        `${API}/api/shop/vouchers/apply`,
        { code, session_id: ensureSessionId() },
        { headers: getAuthHeaders(), withCredentials: true },
      );
      await reloadCart();
      toast.success('Gutschein eingelöst', { description: res.data?.voucher?.code });
      return res.data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gutschein konnte nicht eingelöst werden');
      throw err;
    }
  }, [reloadCart]);

  const removeVoucher = useCallback(async () => {
    try {
      await axios.post(
        `${API}/api/shop/vouchers/remove`,
        { code: cart.voucher_code || '', session_id: ensureSessionId() },
        { headers: getAuthHeaders(), withCredentials: true },
      );
      await reloadCart();
      toast.success('Gutschein entfernt');
    } catch (err) {
      toast.error('Konnte Gutschein nicht entfernen');
    }
  }, [cart.voucher_code, reloadCart]);

  const applyCoupon = useCallback(async (code) => {
    try {
      const res = await axios.post(
        `${API}/api/shop/coupon/apply`,
        { code, session_id: ensureSessionId() },
        { headers: getAuthHeaders(), withCredentials: true },
      );
      const nc = res.data?.cart || { items: [] };
      nc.totals = res.data?.totals || null;
      setCart(nc);
      toast.success('Gutschein aktiviert', { description: code.toUpperCase() });
      return res.data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gutschein konnte nicht eingelöst werden');
      throw err;
    }
  }, []);

  const removeCoupon = useCallback(async () => {
    try {
      const res = await axios.post(
        `${API}/api/shop/coupon/remove`,
        {},
        { headers: getAuthHeaders(), withCredentials: true },
      );
      const nc = res.data?.cart || { items: [] };
      nc.totals = res.data?.totals || null;
      setCart(nc);
      toast.success('Gutschein entfernt');
      return res.data;
    } catch (err) {
      toast.error('Konnte Gutschein nicht entfernen');
    }
  }, []);

  const value = useMemo(
    () => ({
      cart,
      wishlist,
      cartCount,
      wishlistCount,
      totalCents,
      subtotalCents,
      discountCents,
      vipDiscountCents,
      vipPercent,
      netCents,
      taxCents,
      taxBreakdown,
      couponInfo,
      voucherCode,
      voucherCents,
      applyCoupon,
      removeCoupon,
      applyVoucher,
      removeVoucher,
      cartOpen,
      setCartOpen,
      wishlistOpen,
      setWishlistOpen,
      reloadCart,
      reloadWishlist,
      addToCart,
      updateCartItem,
      removeCartItem,
      addToWishlist,
      removeWishlistItem,
      mergeGuestCart,
      sessionId: ensureSessionId(),
    }),
    [cart, wishlist, cartCount, wishlistCount, totalCents, subtotalCents, discountCents, vipDiscountCents, vipPercent, netCents, taxCents, taxBreakdown, couponInfo, voucherCode, voucherCents,
     applyCoupon, removeCoupon, applyVoucher, removeVoucher, cartOpen, wishlistOpen,
     reloadCart, reloadWishlist, addToCart, updateCartItem, removeCartItem,
     addToWishlist, removeWishlistItem, mergeGuestCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
}

export function formatEUR(cents) {
  return ((cents || 0) / 100).toLocaleString('de-DE', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
  });
}

export { ensureSessionId, getAuthHeaders };
